// ABOUTME: SPEC-26 Phase 4 boot-sequence step 1 — Ophion logomark on a dark radial gradient.
// ABOUTME: No asset-prewarm-ready signal exists in this app; shown for a fixed beat, not gated on readiness.

import React, { useState } from 'react';
import CavalryLoaderMark, { LOADER_ANIMATION_DURATION_MS } from './CavalryLoaderMark';

// Honest fixed beat, not a fake "assets ready" abstraction — see DESIGN.md's boot-sequence note.
// Padded slightly past the Cavalry rig's own ~1333 ms runtime so the animation is never cut off
// mid-play on a cold WASM load; if Cavalry fails to init, the static mark below covers the beat.
const LOADER_DISPLAY_MS = LOADER_ANIMATION_DURATION_MS + 150;

// The dedicated "ophion-loadermark@2x.png" export (Figma node 45:1315) was flattened with an opaque
// background baked in — a solid dark rectangle visibly seams against this screen's radial gradient
// instead of sitting transparently on it. The combined icon+wordmark asset used by the gate/header
// (EnterBoutiqueGate/App.js's WORDMARK) is correctly exported with real alpha, and Figma's own loader
// frame is just that same artwork cropped down to its icon glyph — reusing it here (with the same
// crop) fixes the transparency without vendoring a second, broken export.
export const DEFAULT_LOADERMARK_SRC = '/reel-chrome/ophion-wordmark@2x.png';

// Crop geometry from Figma node 45:1315 ("logo+blanco+recortado 1"): an overflow-hidden container
// sized to the icon glyph alone, with the full wordmark image offset/scaled inside it so only the
// icon falls within the visible box — the wordmark's own text sits below, clipped away.
const CROP_ASPECT = 373.333 / 261.333;
const CROP_LEFT_PCT = -72.97;
const CROP_TOP_PCT = 0;
const CROP_WIDTH_PCT = 245.94;
const CROP_HEIGHT_PCT = 191.84;

/**
 * @param {{ brandName?: string, markSrc?: string }} props
 */
function Loader({ brandName = '', markSrc = DEFAULT_LOADERMARK_SRC }) {
  // Falls back to the static cropped wordmark (the pre-animation loader art) if the Cavalry WASM
  // player errors — same asset/crop as before, so a failed init degrades to the old behaviour
  // rather than a blank screen.
  const [cavalryFailed, setCavalryFailed] = useState(false);

  return (
    <div
      role="status"
      aria-label="Loading"
      className="fixed inset-0 w-full h-[100dvh] flex items-center justify-center"
      style={{ background: 'radial-gradient(ellipse at center, rgba(48,48,48,1) 0%, rgba(27,27,27,1) 100%)' }}
    >
      {cavalryFailed ? (
        <div
          className="relative overflow-hidden animate-pulse"
          style={{ aspectRatio: CROP_ASPECT, width: 'clamp(320px, 70vw, 640px)' }}
        >
          <img
            src={markSrc}
            alt={brandName}
            className="absolute max-w-none"
            style={{ left: `${CROP_LEFT_PCT}%`, top: `${CROP_TOP_PCT}%`, width: `${CROP_WIDTH_PCT}%`, height: `${CROP_HEIGHT_PCT}%` }}
          />
        </div>
      ) : (
        // role="img" so the brand label is actually exposed — an aria-label on a bare <div> has
        // no role to attach to and is dropped by most assistive tech, which would leave the
        // Cavalry canvas as an unnamed graphic.
        <div role="img" aria-label={brandName} style={{ width: 'clamp(320px, 70vw, 640px)' }}>
          <CavalryLoaderMark maxWidth="100%" onError={() => setCavalryFailed(true)} />
        </div>
      )}
    </div>
  );
}

export { LOADER_DISPLAY_MS };
export default Loader;
